/*
  How a calendar credential gets connected, as cards rather than one long
  dropdown.

  The old form put ten Exchange versions, Google and Office 365 in a single
  select, so an operator had to know that picking "Office 365" swaps the whole
  rest of the form. The choice is really two questions:

    1. automatic (sign in at Google / Microsoft) or manual (an Exchange server
       reached with a username, password and host)?
    2. which one?

  Only the manual path needs the version dropdown, so it is the only path that
  shows one - and the parent form only renders username / password / host when
  this reports "manual".

  Styled with sx / inline styles rather than makeStyles, for the same reason
  CalendarOAuthPanel is: @material-ui/core v4 and @mui/material v5 run side by
  side here and their makeStyles share a class name namespace.
*/

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import BoltIcon from "@mui/icons-material/Bolt";
import DnsIcon from "@mui/icons-material/Dns";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FcGoogle } from "react-icons/fc";
import { SiMicrosoftoffice } from "react-icons/si";

export const CONNECTION_METHODS = { AUTOMATIC: "automatic", MANUAL: "manual" };

export const OAUTH_CALENDAR_TYPES = ["Google Calendar", "Office 365"];

export const EXCHANGE_CALENDAR_TYPES = [
  "Exchange2016",
  "Exchange2015",
  "Exchange2013",
  "Exchange2013_SP1",
  "Exchange2010",
  "Exchange2010_SP1",
  "Exchange2010_SP2",
  "Exchange2007_SP1",
];

/**
 * Which half of the form a stored calendar type belongs to. Used by the edit
 * form, which is handed a type and has to work backwards to the method.
 */
export const methodForCalendarType = (calendarType) => {
  if (!calendarType) return "";
  return OAUTH_CALENDAR_TYPES.includes(calendarType)
    ? CONNECTION_METHODS.AUTOMATIC
    : CONNECTION_METHODS.MANUAL;
};

const SelectableCard = ({
  selected,
  disabled,
  onClick,
  icon,
  title,
  caption,
  compact = false,
}) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-pressed={selected}
    aria-disabled={disabled}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(event) => {
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    sx={{
      position: "relative",
      flex: 1,
      minWidth: compact ? 170 : 200,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 0.75,
      px: 2,
      py: compact ? 1.75 : 2.5,
      borderRadius: 2,
      border: "2px solid",
      borderColor: selected ? "primary.main" : "divider",
      backgroundColor: selected
        ? "rgba(25, 118, 210, 0.06)"
        : "background.paper",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled && !selected ? 0.5 : 1,
      transition: "border-color .15s, box-shadow .15s, transform .15s",
      "&:hover": disabled
        ? {}
        : {
            borderColor: "primary.main",
            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            transform: "translateY(-1px)",
          },
      "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
    }}
  >
    {selected && (
      <CheckCircleIcon
        color="primary"
        fontSize="small"
        sx={{ position: "absolute", top: 8, right: 8 }}
      />
    )}
    <Box
      sx={{
        fontSize: compact ? 28 : 34,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "primary.main",
      }}
    >
      {icon}
    </Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    {caption ? (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {caption}
      </Typography>
    ) : null}
  </Box>
);

/**
 * @param {string}   method         "" | "automatic" | "manual"
 * @param {string}   calendarType   the exact value stored on the calendar row
 * @param {function} onMethodChange called with the new method; the parent is
 *                                  expected to clear the fields the other half
 *                                  of the form owns
 * @param {function} onCalendarTypeChange called with the new calendar type
 * @param {boolean}  locked         render the current choice read-only, for the
 *                                  edit form where the type cannot change
 * @param {boolean}  disabled
 */
const CalendarMethodPicker = ({
  method,
  calendarType,
  onMethodChange,
  onCalendarTypeChange,
  locked = false,
  disabled = false,
}) => {
  const isAutomatic = method === CONNECTION_METHODS.AUTOMATIC;
  const isManual = method === CONNECTION_METHODS.MANUAL;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <SelectableCard
          selected={isAutomatic}
          disabled={disabled || locked}
          onClick={() => onMethodChange(CONNECTION_METHODS.AUTOMATIC)}
          icon={<BoltIcon fontSize="inherit" />}
          title="Automatic"
          caption="Sign in with Google or Microsoft"
        />
        <SelectableCard
          selected={isManual}
          disabled={disabled || locked}
          onClick={() => onMethodChange(CONNECTION_METHODS.MANUAL)}
          icon={<DnsIcon fontSize="inherit" />}
          title="Manual"
          caption="Exchange server credentials"
        />
      </Box>

      {isAutomatic && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, display: "block", mb: 1 }}
          >
            Choose a provider
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <SelectableCard
              compact
              selected={calendarType === "Google Calendar"}
              disabled={disabled || locked}
              onClick={() => onCalendarTypeChange("Google Calendar")}
              icon={<FcGoogle />}
              title="Google Calendar"
              caption="Google Workspace"
            />
            <SelectableCard
              compact
              selected={calendarType === "Office 365"}
              disabled={disabled || locked}
              onClick={() => onCalendarTypeChange("Office 365")}
              icon={<SiMicrosoftoffice color="#D83B01" />}
              title="Office 365"
              caption="Microsoft 365 / Outlook"
            />
          </Box>
        </Box>
      )}

      {isManual && (
        <Box sx={{ mt: 2 }}>
          <TextField
            required
            select
            fullWidth
            id="calendarType"
            name="calendarType"
            size="small"
            label="Exchange version"
            variant="outlined"
            disabled={disabled || locked}
            value={
              EXCHANGE_CALENDAR_TYPES.includes(calendarType) ? calendarType : ""
            }
            onChange={(event) => onCalendarTypeChange(event.target.value)}
          >
            {EXCHANGE_CALENDAR_TYPES.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    </Box>
  );
};

export default CalendarMethodPicker;
